import { useCallback } from 'react';
import { useSubscription } from './useSubscription';

interface ValidationResult {
  canProceed: boolean;
  reason?: 'no_plan' | 'no_credits' | 'payment_failed' | 'expired' | 'loading';
}

/**
 * Subscription validation hook without console logging
 * Consolidated version that uses useSubscription for all data
 */
export const useSubscriptionValidation = () => {
  const { subscription, loading: subscriptionLoading, creditsData } = useSubscription();

  const validateVoiceAccess = useCallback((): ValidationResult => {
    // Still loading data
    if (subscriptionLoading || !subscription) {
      return { canProceed: false, reason: 'loading' };
    }

    // No subscription at all
    if (!subscription.subscribed) {
      return { canProceed: false, reason: 'no_plan' };
    }

    // Payment issues
    if (subscription.needsPayment) {
      return { canProceed: false, reason: 'payment_failed' };
    }

    // Subscription expired
    if (subscription.isExpired) {
      return { canProceed: false, reason: 'expired' };
    }

    // No credits for voice - use consolidated credit data
    if (!creditsData?.credits_left || creditsData.credits_left <= 0) {
      return { canProceed: false, reason: 'no_credits' };
    }

    return { canProceed: true };
  }, [subscription, creditsData, subscriptionLoading]);

  const validateChatAccess = useCallback((): ValidationResult => {
    // Still loading data
    if (subscriptionLoading || !subscription) {
      return { canProceed: false, reason: 'loading' };
    }

    // No subscription at all
    if (!subscription.subscribed) {
      return { canProceed: false, reason: 'no_plan' };
    }

    // Payment issues
    if (subscription.needsPayment) {
      return { canProceed: false, reason: 'payment_failed' };
    }

    // Subscription expired
    if (subscription.isExpired) {
      return { canProceed: false, reason: 'expired' };
    }

    // Chat access doesn't require credits - just valid subscription
    return { canProceed: true };
  }, [subscription, subscriptionLoading]);

  const validateCreditsForAction = useCallback((): ValidationResult => {
    // Still loading data
    if (subscriptionLoading) {
      return { canProceed: false, reason: 'loading' };
    }

    // Just check if user has credits (for any action that consumes credits)
    if (!creditsData?.credits_left || creditsData.credits_left <= 0) {
      return { canProceed: false, reason: 'no_credits' };
    }

    return { canProceed: true };
  }, [creditsData, subscriptionLoading]);

  return {
    validateVoiceAccess,
    validateChatAccess,
    validateCreditsForAction,
    hasActiveSubscription: subscription?.subscribed || false,
    hasCredits: (creditsData?.credits_left || 0) > 0,
    isLoading: subscriptionLoading,
  };
};
